import React, { useRef, useState } from "react";
import { MenuProps } from "@mui/material";

import {
  ArrayUnion,
  FileEntity,
  Space,
} from "@highnote/server/src/core/entities";
import {
  APP_FEATURES,
  AppFeaturesStatus,
} from "@highnote/server/src/core/features";
import {
  MAX_TRACKS_ERROR,
  PERMISSION,
} from "@highnote/server/src/core/shared-util";
import { highnote } from "@highnote/server/src/sdk";
import { ErrorBoundary } from "App/common/ErrorBoundary";
import { AudioFileSelect } from "App/common/FileSelect";
import { ReactComponent as PlusSVG } from "App/common/icons/plus.svg";
import { ReactComponent as UploadSVG } from "App/common/icons/upload.svg";
import { ReactComponent as VoiceSVG } from "App/common/icons/voice.svg";
import { Menu, MenuItem } from "App/common/Menu";
import {
  LIMIT_TYPE,
  usePlanLimitsContext,
} from "App/common/PlanLimits/usePlanLimits";
import { useToast } from "App/common/useToast";
import { useAuth } from "App/components/Auth";
import { JOIN_ENTITY_TRIGGER } from "App/components/Auth/util";
import { PermissionTooltip } from "App/components/PermissionTooltip";
import { RecordDialog } from "App/components/RecordDialog";
import {
  AudioUploadFileSizeErr,
  UPLOAD_GROUP,
  useFiles,
} from "App/components/useFiles";
import {
  Button,
  BUTTON_SIZE,
  BUTTON_THEME,
  ButtonProps,
} from "App/core/Button";
import { useTrack } from "../../useEntities/useTrack";
import { useTrackVersionEditorContext } from "App/components/TrackVersionEditor/utils/context/TrackVersionEditorContext";

export const AddVersionButton = ({
  space,
  multiple,
  menuProps,
  additionalMenuOptions,
  className,
  ...rest
}: {
  space: Space;
  multiple?: boolean;
  menuProps?: Partial<MenuProps>;
  additionalMenuOptions?: React.ReactNode[];
} & ButtonProps) => {
  const anchorRef = useRef<HTMLButtonElement>();
  const { isAllowed, isPublicView, joinEntity } = useAuth();
  const { track, isBorrowed } = useTrack();
  const { addErrorMessage } = useToast();
  const { uploadFiles, getUploadCache } = useFiles();
  const uploadCache = getUploadCache(
    UPLOAD_GROUP.TRACK_VERSIONS_UNPINNED,
    track?.id,
  );
  const { showPlanLimitsDialog } = usePlanLimitsContext();
  const { openTrackVersionEditor, viewingSpaceId } =
    useTrackVersionEditorContext();
  const [isMenuOpen, setMenuOpen] = useState<boolean>(false);
  const [isRecorderOpen, setRecorderOpen] = useState<boolean>(false);

  const canAddToTrack = isAllowed(PERMISSION.TO_ADD_TO_TRACK_IN_SPACE, {
    space,
    track,
  });

  const addVersionToTrack = async (file: FileEntity) => {
    try {
      await highnote.updateTrack({
        id: track.id,
        data: {
          versionFilesV2: new ArrayUnion([file.id]) as unknown as Id[],
        },
      });

      joinEntity({
        entity: space,
        entityType: "Space",
        trigger: JOIN_ENTITY_TRIGGER.UPLOAD,
      });
    } catch (e) {
      if (e.message === MAX_TRACKS_ERROR) {
        showPlanLimitsDialog(LIMIT_TYPE.TRACKS);
        return;
      }
      throw e;
    }
  };

  const handleFileSelect = async (files: File[]) => {
    setMenuOpen(false);

    if (!files) {
      return;
    }

    try {
      const uploadPayloads = files.map((file) => {
        return {
          file,
          cache: uploadCache,
        };
      });
      await uploadFiles({
        payloads: uploadPayloads,
        onSuccess: async (_, fileEntity) => {
          await addVersionToTrack(fileEntity);
        },
        onError: (e) => {
          if (e instanceof AudioUploadFileSizeErr) {
            addErrorMessage(e.message, { title: e.title });
            return;
          }
          throw e;
        },
      });
    } catch (e) {
      console.log(e);
    }
  };

  if (
    isPublicView(space) &&
    !isAllowed(PERMISSION.TO_ADD_TO_TRACK_IN_SPACE, { space, track })
  ) {
    return null;
  }

  // TODO: Maybe move this inside TrackVersionTabs once we fully migrate to the new track version editor
  // It does not need to abstracted out as a component.
  if (AppFeaturesStatus[APP_FEATURES.TRACK_VERSION_EDITOR]) {
    return (
      <PermissionTooltip hasPermission={canAddToTrack}>
        <Button
          size={BUTTON_SIZE.XSMALL}
          disabled={rest.disabled || !canAddToTrack}
          className="AddVersionMenu-button add-version-button add-version"
          data-cypress-id="add-version-btn"
          theme={BUTTON_THEME.SECONDARY}
          onClick={() => {
            openTrackVersionEditor({
              type: "pin",
              track,
              isBorrowed,
              viewingSpaceId,
            });
          }}
        >
          <PlusSVG /> <span>Version</span>
        </Button>
      </PermissionTooltip>
    );
  }

  return (
    <ErrorBoundary name="AddVersionMenu">
      <PermissionTooltip hasPermission={canAddToTrack}>
        <Button
          {...rest}
          disabled={rest.disabled || !canAddToTrack}
          className={`AddVersionMenu-button ${className || ""}`}
          onClick={() => setMenuOpen(true)}
          ref={anchorRef}
        />
      </PermissionTooltip>

      <Menu
        className={`add-version-menu ${
          menuProps?.className ? `${menuProps?.className}-menu` : ""
        }`}
        anchorEl={anchorRef.current}
        onClose={() => setMenuOpen(false)}
        open={isMenuOpen}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        {...(menuProps || {})}
      >
        {...additionalMenuOptions || []}

        <MenuItem>
          <AudioFileSelect
            // disabled={!!disabled}
            onSelect={handleFileSelect}
            allowMultiple={multiple}
          >
            <div>
              <UploadSVG /> Upload new version
            </div>
          </AudioFileSelect>
        </MenuItem>

        <MenuItem
          onClick={() => {
            setMenuOpen(false);
            setRecorderOpen(true);
          }}
        >
          <VoiceSVG />
          Record new version
        </MenuItem>
      </Menu>

      {isRecorderOpen && (
        <RecordDialog
          cache={uploadCache}
          onClose={() => setRecorderOpen(false)}
          onSave={addVersionToTrack}
        />
      )}
    </ErrorBoundary>
  );
};
